import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wine, Plus, TrendingUp, AlertTriangle, RefreshCw, Search, Filter } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { promoSummerOrderService, PROMO_STATUS_LABELS } from '../../services/promoSummerOrderService';
import PromoOrderForm from './components/PromoOrderForm';
import PromoOrderList from './components/PromoOrderList';
import PromoFinancialView from './components/PromoFinancialView';
import PromoOrderDetail from './components/PromoOrderDetail';
import Icon from '../../components/AppIcon';


const TABS = [
  { id: 'orders', label: 'Commandes', icon: Wine },
  { id: 'financial', label: 'Vue Financière', icon: TrendingUp },
  { id: 'relances', label: 'Relances', icon: AlertTriangle },
];

export default function PromoSummerOrdersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [relanceOrders, setRelanceOrders] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [relanceDays, setRelanceDays] = useState(30);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [ordersData, relanceData, summaryData] = await Promise.all([
        promoSummerOrderService?.getAll({ status: statusFilter, search: searchTerm }),
        promoSummerOrderService?.getOrdersNeedingRelance(relanceDays),
        promoSummerOrderService?.getFinancialSummary(),
      ]);
      setOrders(ordersData);
      setRelanceOrders(relanceData);
      setFinancialSummary(summaryData);
    } catch (err) {
      console.error('Erreur chargement commandes promo:', err);
      setError(err?.message || 'Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, relanceDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOrderCreated = async (newOrder) => {
    setShowForm(false);
    await loadData();
  };

  const handleOrderUpdated = async () => {
    setSelectedOrder(null);
    await loadData();
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
  };

  const handleRelance = async (orderId) => {
    try {
      await promoSummerOrderService?.markRelance(orderId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la relance');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <BreadcrumbTrail
        customItems={[
          { label: 'Administration', path: '/admin-dashboard' },
          { label: 'Barriques & Cartons', path: '/unified-sales-management' },
          { label: 'Commandes Promo Été 2026', path: '/promo-summer-orders' },
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <Wine className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Commandes Promo Été 2026
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Barriques 270L — 160€ TTC · Acompte 50€ · Solde 110€
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {relanceOrders?.length > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                <AlertTriangle className="h-4 w-4" />
                {relanceOrders?.length} relance{relanceOrders?.length > 1 ? 's' : ''} en attente
              </span>
            )}
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Nouvelle commande
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* KPI Strip */}
        {financialSummary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              {
                label: 'Commandes actives',
                value: financialSummary?.total_orders || 0,
                sub: `${financialSummary?.total_barrels || 0} barriques`,
                color: 'text-amber-600',
                bg: 'bg-amber-50 dark:bg-amber-900/20',
              },
              {
                label: 'Acomptes encaissés',
                value: `${Number(financialSummary?.total_deposit_collected || 0)?.toFixed(0)}€`,
                sub: `ADT: ${Number(financialSummary?.total_adt_received || 0)?.toFixed(0)}€`,
                color: 'text-green-600',
                bg: 'bg-green-50 dark:bg-green-900/20',
              },
              {
                label: 'Dû prestataires',
                value: `${Number(financialSummary?.total_provider_due || 0)?.toFixed(0)}€`,
                sub: '30€ / barrique',
                color: 'text-orange-600',
                bg: 'bg-orange-50 dark:bg-orange-900/20',
              },
              {
                label: 'Solde futur à encaisser',
                value: `${Number(financialSummary?.total_future_balance || 0)?.toFixed(0)}€`,
                sub: `CA total attendu: ${Number(financialSummary?.total_expected_revenue || 0)?.toFixed(0)}€`,
                color: 'text-blue-600',
                bg: 'bg-blue-50 dark:bg-blue-900/20',
              },
            ]?.map((kpi) => (
              <div key={kpi?.label} className={`${kpi?.bg} rounded-xl p-4 border border-gray-100 dark:border-slate-700`}>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{kpi?.label}</p>
                <p className={`text-xl font-bold ${kpi?.color}`}>{kpi?.value}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{kpi?.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white dark:bg-slate-800 rounded-xl p-1 border border-gray-200 dark:border-slate-700 w-fit">
          {TABS?.map((tab) => {
            const Icon = tab?.icon;
            const isActive = activeTab === tab?.id;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab?.label}
                {tab?.id === 'relances' && relanceOrders?.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {relanceOrders?.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'orders' && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher client, téléphone, adresse..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value)}
                  className="border border-gray-200 dark:border-slate-600 rounded-lg text-sm px-3 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">Tous les statuts</option>
                  {Object.entries(PROMO_STATUS_LABELS)?.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <PromoOrderList
              orders={orders}
              loading={loading}
              onSelect={handleSelectOrder}
              onRefresh={loadData}
            />
          </>
        )}

        {activeTab === 'financial' && (
          <PromoFinancialView summary={financialSummary} orders={orders} loading={loading} />
        )}

        {activeTab === 'relances' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Seuil d'alerte :
              </label>
              <select
                value={relanceDays}
                onChange={(e) => setRelanceDays(Number(e?.target?.value))}
                className="border border-gray-200 dark:border-slate-600 rounded-lg text-sm px-3 py-2 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              >
                <option value={7}>7 jours</option>
                <option value={14}>14 jours</option>
                <option value={30}>30 jours</option>
                <option value={45}>45 jours</option>
                <option value={60}>60 jours</option>
              </select>
              <span className="text-sm text-gray-500">sans activité</span>
            </div>

            {relanceOrders?.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Aucune relance nécessaire pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {relanceOrders?.map((order) => (
                  <div
                    key={order?.id}
                    className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="font-semibold text-gray-900 dark:text-white">{order?.client_name}</span>
                        <span className="text-sm text-gray-500">{order?.client_phone}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {order?.barrel_quantity} barrique(s) · Livré le{' '}
                        {order?.delivery_date
                          ? new Date(order.delivery_date)?.toLocaleDateString('fr-FR')
                          : '—'}
                      </p>
                      <p className="text-xs text-red-500 mt-1">
                        En attente depuis le{' '}
                        {new Date(order?.updated_at)?.toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSelectOrder(order)}
                        className="px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        Voir fiche
                      </button>
                      <button
                        onClick={() => handleRelance(order?.id)}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Marquer relancé
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* New Order Form Modal */}
      {showForm && (
        <PromoOrderForm
          onClose={() => setShowForm(false)}
          onSaved={handleOrderCreated}
        />
      )}
      {/* Order Detail Modal */}
      {selectedOrder && (
        <PromoOrderDetail
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}
